// @ts-strict-ignore
import type { Attribute, ServiceObject } from 'flow/store/engine/types';
import type { ServiceAttributeKey } from './types';

export function indexServiceAttributesById(attributes: Attribute[]): Record<number, Attribute> {
  return Object.fromEntries(
    attributes.map(attribute => [attribute.attribute_id, attribute]),
  );
}

export function indexAttributesByToken(attributes: Attribute[]): Record<Attribute['attribute_token'], Attribute> {
  return Object.fromEntries(
    attributes.map(attribute => [attribute.attribute_token, attribute]),
  );
}


/**
 * Creates an index of all services' internal names by any of their attributes' token.
 * @example
 * {
 *  token_1: "service-a",
 *  token_2: "service-a",
 *  token_3: "service-b",
 *  token_4: "service-b",
 *  token_5: "service-c",
 *  token_6: "service-c",
 * }
 */
export function indexServiceInternalNamesByAttributeToken(
  services: ServiceObject[],
): Record<Attribute['attribute_token'], ServiceObject['internal_name']> {
  return Object.fromEntries(
    services.flatMap(service => {
      const serviceAttributes = getAttributesForServices(service);
      return serviceAttributes.map(attribute => [attribute.attribute_token, service.internal_name]);
    }),
  );
}


/**
 * Creates an index of all service attributes by their unique key.
 * @example
 * {
 *  "service-a::selector-1::display-name": attribute-1,
 *  "service-a::selector-2::display-name": attribute-2,
 *  "service-b::selector-1::display-name": attribute-3,
 *  "service-b::selector-2::display-name": attribute-4,
 * }
 */
export function indexAttributesByUniqueKey(
  services: ServiceObject[],
): Record<ServiceAttributeKey, Attribute> {
  return Object.fromEntries(
    services.flatMap(service => {
      const attributesForService = getAttributesForServices(service);
      const attributeEntriesForService = attributesForService.map(attribute => {
        const serviceAttributeKey = createServiceAttributeKey({
          attributeSelector: attribute.selector,
          serviceName: service.internal_name,
          attributeDisplayName: attribute.display_name,
        });
        return [serviceAttributeKey, attribute];
      });

      return attributeEntriesForService;
    }),
  );
}


/**
 * The Attributes DB table enforces a unique index on: (service_id, selector, display_name), so we
 * use those fields as the unique key
 */
export function createServiceAttributeKey(args: {
  serviceName: ServiceObject['internal_name'];
  attributeSelector: Attribute['selector'];
  attributeDisplayName: Attribute['display_name'];
}): ServiceAttributeKey {
  const { serviceName, attributeSelector, attributeDisplayName } = args;
  return `${serviceName}::${attributeSelector}::${attributeDisplayName}`;
}


export function getAttributesForServices(services: ServiceObject | ServiceObject[]): Attribute[] {
  const array = Array.isArray(services) ? services : [services];
  return array.flatMap(service => Object.values(service.attributes));
}
